/* eslint-disable @typescript-eslint/no-explicit-any */

import React from "react";

import _ from "lodash";

import type {
    CheckErrorVisibilityInterface,
    FormHookType,
    HideAllErrorInterface,
    HideErrorInterface,
    ReloadToInitialValuesInterface,
    SetValueInterface,
    ShowAllErrorInterface,
    ShowErrorInterface,
    UnsetValueInterface,
    UpdateFormInterface,
} from "../types/form";
import type {
    ErrorsType,
    ErrorsVisibilityType,
    ObjectPropertyType,
    ValidationErrorType,
} from "../types/validation";
import { loadFormStructure, setValueByPath, unsetValueByPath } from "../utils/form";
import validate, { getErrorDescription } from "../utils/validate";

const hideRecursiveError = (hideError: HideErrorInterface, form: any, prevKey: string) => {
    _.forEach(form, (item, key) => {
        if (_.isObject(item) || _.isArray(item)) {
            hideRecursiveError(hideError, item, prevKey + key + ".");
        }

        hideError(prevKey + key);
    });
};

function useForm<T extends object>(
    schema: ObjectPropertyType,
    initialValue?: T,
    getExtraErrorDescription?: (errorCode: string) => string,
    lang?: string
): FormHookType<T> {
    const [isValid, setIsValid] = React.useState<boolean>(false);
    const [showAllErrorCheck, setShowAllError] = React.useState<boolean>(false);

    const schemaMemoized = React.useMemo(() => schema, [schema]);

    const formStructureMemoized = React.useMemo<T>(
        () => initialValue ?? loadFormStructure(schemaMemoized),
        [schemaMemoized, initialValue]
    );

    const [form, setForm] = React.useState<T>(formStructureMemoized);

    const [errorsVisibility, setErrorsVisibility] = React.useState<ErrorsVisibilityType>({});

    const [errors, setErrors] = React.useState<ErrorsType>({});

    React.useEffect(() => {
        const calculatedErrors: Array<ValidationErrorType> = validate(schemaMemoized, form);

        setErrors(() => {
            const newErrors: ErrorsType = {};

            calculatedErrors.forEach((error) => {
                if (!(error.path in newErrors)) {
                    newErrors[error.path] = [];
                }

                newErrors[error.path].push({
                    ...error,
                });
            });

            _.forEach(newErrors, (tmpErrors, key) => {
                const notEmptyFinded = tmpErrors.find((err) => {
                    return err.path === key && err.code === "notEmpty";
                });

                if (notEmptyFinded) {
                    newErrors[key] = [notEmptyFinded];
                }
            });

            return newErrors;
        });

        setIsValid(calculatedErrors.length === 0);
    }, [schemaMemoized, form]);

    const setValue: SetValueInterface = React.useCallback(
        (path: string, value: any) =>
            setForm((prevForm: any) => {
                const newForm: any = _.cloneDeep(prevForm);

                setValueByPath(newForm, path, value);

                return newForm;
            }),
        []
    );

    const unsetValue: UnsetValueInterface = React.useCallback((path: string) => {
        setForm((prevForm: any) => {
            const newForm: any = _.cloneDeep(prevForm);

            unsetValueByPath(newForm, path);

            return newForm;
        });
    }, []);

    const updateForm: UpdateFormInterface = React.useCallback(
        (pathValues: { [path: string]: any }) => {
            setForm((prevForm: any) => {
                const newForm: any = _.cloneDeep(prevForm);

                _.forEach(pathValues, (value, path) => {
                    setValueByPath(newForm, path, value);
                });

                return newForm;
            });
        },
        []
    );

    const changeErrorVisibilty = React.useCallback((path: string, value: boolean) => {
        setErrorsVisibility((prevErrorsVisibility: any) => {
            return {
                ...prevErrorsVisibility,
                [path]: value,
            };
        });
    }, []);

    const hideError: HideErrorInterface = React.useCallback(
        (path: string) => {
            changeErrorVisibilty(path, false);
        },
        [changeErrorVisibilty]
    );

    const showError: ShowErrorInterface = React.useCallback(
        (path: string) => {
            changeErrorVisibilty(path, true);
        },
        [changeErrorVisibilty]
    );

    const showAllError: ShowAllErrorInterface = React.useCallback(() => {
        setShowAllError(true);
    }, []);

    const hideAllError: HideAllErrorInterface = React.useCallback(() => {
        hideRecursiveError(hideError, form, "");

        setShowAllError(false);
    }, [hideError, form]);

    const checkErrorVisibility: CheckErrorVisibilityInterface = React.useCallback(
        (path: string): Array<string> => {
            if (errors[path]) {
                if (errorsVisibility[path] || showAllErrorCheck) {
                    const errorDescriptions = errors[path].map((errorCode: ValidationErrorType) =>
                        getErrorDescription(errorCode, lang)
                    );

                    if (getExtraErrorDescription !== undefined) {
                        return errorDescriptions.map(getExtraErrorDescription);
                    } else {
                        return errorDescriptions;
                    }
                } else {
                    return [];
                }
            } else {
                return [];
            }
        },
        [getExtraErrorDescription, errors, errorsVisibility, showAllErrorCheck, lang]
    );

    const reloadToInitialValues: ReloadToInitialValuesInterface = React.useCallback(() => {
        setForm(formStructureMemoized);

        hideAllError();
    }, [hideAllError, formStructureMemoized]);

    const hasUnsavedChanges: boolean = React.useMemo(
        () => JSON.stringify(form) !== JSON.stringify(formStructureMemoized),
        [form, formStructureMemoized]
    );

    return React.useMemo(
        () => ({
            setValue,
            unsetValue,
            setForm,
            updateForm,
            hideError,
            showError,
            checkErrorVisibility,
            showAllError,
            hideAllError,
            reloadToInitialValues,
            form,
            isValid,
            errors,
            errorsVisibility,
            allErrorsVisibility: showAllErrorCheck,
            hasUnsavedChanges,
        }),
        [
            setValue,
            unsetValue,
            setForm,
            updateForm,
            hideError,
            showError,
            checkErrorVisibility,
            showAllError,
            hideAllError,
            reloadToInitialValues,
            form,
            isValid,
            errors,
            errorsVisibility,
            showAllErrorCheck,
            hasUnsavedChanges,
        ]
    );
}

export default useForm;
